import { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_TESTIMONIALS = [
  {
    id: '1',
    author: 'Sarah M.',
    pet_name: 'Cooper',
    content: 'Cooper and I get compliments everywhere we go! The quality is amazing and the fit is perfect for both of us.',
    rating: 5,
    image_url: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=200',
  },
  {
    id: '2',
    author: 'Mike T.',
    pet_name: 'Luna',
    content: "Finally, matching outfits that don't look cheesy! Luna loves her sweater and the fabric is super soft.",
    rating: 5,
    image_url: 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=200',
  },
  {
    id: '3',
    author: 'Emma R.',
    pet_name: 'Max',
    content: 'The matching bundle was worth every penny. Max has never looked more handsome, and I feel stylish too!',
    rating: 5,
    image_url: 'https://images.unsplash.com/photo-1544568100-847a948585b9?w=200',
  },
];

export const Testimonials = () => {
  const [testimonials, setTestimonials] = useState(DEFAULT_TESTIMONIALS);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        if (response.data && response.data.length > 0) {
          setTestimonials(response.data);
        }
      } catch (error) {
        console.log('Using default testimonials');
      }
    };
    fetchTestimonials();
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section
      data-testid="testimonials-section"
      className="py-24 bg-[#f5f0e6]"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="font-accent text-2xl text-[#c68b4d] block mb-2">What They Say</span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#1e3a5f]">
            Pawsitive Reviews
          </h2>
        </div>

        {/* Testimonial Card */}
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-white rounded-3xl shadow-lg p-8 md:p-12">
            {/* Quote Icon */}
            <div className="absolute -top-6 left-8 w-12 h-12 bg-[#e76f51] rounded-full flex items-center justify-center">
              <Quote className="w-6 h-6 text-white" />
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-center">
              {/* Image */}
              <div className="flex-shrink-0">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-[#c68b4d]/20">
                  <img
                    src={currentTestimonial.image_url}
                    alt={currentTestimonial.pet_name}
                    className="w-full h-full object-cover"
                    data-testid="testimonial-image"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 text-center md:text-left">
                {/* Rating */}
                <div className="flex justify-center md:justify-start gap-1 mb-4">
                  {[...Array(currentTestimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-[#c68b4d] fill-current"
                    />
                  ))}
                </div>

                {/* Quote */}
                <p
                  data-testid="testimonial-content"
                  className="font-body text-lg md:text-xl leading-relaxed text-[#1e3a5f] mb-6"
                >
                  "{currentTestimonial.content}"
                </p>

                {/* Author */}
                <div>
                  <p className="font-heading text-lg font-semibold text-[#1e3a5f]">
                    {currentTestimonial.author}
                  </p>
                  <p className="font-body text-sm text-[#1e3a5f]/60">
                    & {currentTestimonial.pet_name} 🐕
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-center gap-4 mt-8">
              <Button
                data-testid="testimonial-prev-btn"
                onClick={prevSlide}
                variant="outline"
                size="icon"
                className="rounded-full border-[#1e3a5f]/20 hover:bg-[#1e3a5f] hover:text-white hover:border-[#1e3a5f]"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              {/* Dots */}
              <div className="flex items-center gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex
                        ? 'w-8 bg-[#e76f51]'
                        : 'bg-[#1e3a5f]/20 hover:bg-[#1e3a5f]/40'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>

              <Button
                data-testid="testimonial-next-btn"
                onClick={nextSlide}
                variant="outline"
                size="icon"
                className="rounded-full border-[#1e3a5f]/20 hover:bg-[#1e3a5f] hover:text-white hover:border-[#1e3a5f]"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
